// Workspace work-tab bar (PRD-0085, slice 1).
//
// Pinned Home tab + closeable work tabs (kind badge + label + ✕) + a `+` that
// opens Home. Mirrors the reference mockup's `.tabbar` / `.wtab` styles
// (artifacts/operator-brief-report-prototypes/0084-operator-brief-report.html)
// using the app's Tailwind tokens (brand-blue accent, light panel, slate lines).

import { cn } from "../../lib/utils.ts";
import { useWorkspaceTabs } from "../../contexts/WorkspaceTabsContext";
import { HOME_TAB, type WorkTabKind } from "../../lib/workspace-tabs";
import { stopAgentStream } from "../../lib/agent-stream-lifecycle";

/** Single-letter (or glyph) kind badge, matching the mockup's `.wtab-kind`. */
const KIND_GLYPH: Record<WorkTabKind, string> = {
  home: "⌂",
  brief: "B",
  review: "R",
  output: "O",
  draft: "D",
  artifact: "A",
  docs: "P",
  assets: "M",
  tasks: "T",
  workflows: "F",
  chat: "C",
  analytics: "S",
};

const KIND_BADGE_CLASS: Record<WorkTabKind, string> = {
  home: "bg-slate-900 text-white",
  brief: "bg-brand-100 text-brand-700",
  review: "bg-green-50 text-success",
  output: "bg-slate-100 text-slate-600",
  draft: "bg-amber-50 text-amber-700",
  artifact: "bg-violet-50 text-violet-700",
  docs: "bg-brand-100 text-brand-700",
  assets: "bg-brand-100 text-brand-700",
  tasks: "bg-brand-100 text-brand-700",
  workflows: "bg-brand-100 text-brand-700",
  chat: "bg-brand-100 text-brand-700",
  analytics: "bg-brand-100 text-brand-700",
};

export function WorkspaceTabBar() {
  const { tabs, activeId, closeTab, activateTab } = useWorkspaceTabs();

  return (
    <div
      role="tablist"
      aria-label="Open work tabs"
      className="sticky top-0 z-40 flex min-h-[52px] items-stretch justify-between gap-4 border-b border-slate-200 bg-slate-50/95 pr-3.5 backdrop-blur"
    >
      <div className="flex min-w-0 items-end gap-0.5">
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          const isHome = tab.id === HOME_TAB.id;
          return (
            // role=tab on a div (not a <button>) because the close control is a
            // descendant <button>, and nesting button>button is invalid HTML. We add
            // tabIndex + keyboard handlers to keep it focusable/operable.
            <div
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              tabIndex={0}
              title={tab.label}
              onClick={() => activateTab(tab.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activateTab(tab.id);
                }
              }}
              className={cn(
                "relative flex h-12 cursor-pointer items-center gap-2.5 rounded-t-lg border border-b-0 border-transparent px-3 text-[13px] text-slate-500",
                isHome ? "min-w-0" : "min-w-[150px] max-w-[260px]",
                isActive && "border-slate-200 bg-white font-semibold text-slate-900",
              )}
            >
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-[3px] rounded-t-lg bg-brand-600"
                />
              )}
              <span
                aria-hidden
                className={cn(
                  "grid h-[22px] min-w-[22px] place-items-center rounded-md font-mono text-[10px] font-medium",
                  KIND_BADGE_CLASS[tab.kind],
                )}
              >
                {KIND_GLYPH[tab.kind]}
              </span>
              <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{tab.label}</span>
              {!isHome && (
                <button
                  type="button"
                  aria-label={`Close ${tab.label}`}
                  className="ml-auto shrink-0 text-xs text-slate-400 hover:text-slate-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    // A route change backgrounds an open chat tab; only its
                    // explicit close control cancels the registered SSE work.
                    if (tab.kind === "chat") {
                      void stopAgentStream(tab.id.slice("chat:".length)).catch((error) => {
                        // Closing is intentionally non-blocking, but retain an
                        // actionable diagnostic if the server could not stop.
                        console.error("Failed to stop the closed chat stream", error);
                      });
                    }
                    closeTab(tab.id);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-slate-400">workspace</span>
      </div>
    </div>
  );
}
